const DEFAULT_VIEW = 'overview';

export const VIEW_TO_PATH = {
  overview: '/overview',
  analytics: '/analytics',
  ai: '/ai',
  seats: '/seats',
  billing: '/billing',
  admins: '/team',
  branding: '/branding',
  audit: '/audit-log',
  support: '/support',
};

const PATH_TO_VIEW = Object.fromEntries(
  Object.entries(VIEW_TO_PATH).map(([view, path]) => { return [path, view]; }),
);

const normalizePathname = (pathname = '') => {
  const normalized = `/${String(pathname).replace(/^\/+|\/+$/g, '')}`;
  return normalized === '/' ? '/' : normalized;
};

export function readPortalView(pathname) {
  const path = normalizePathname(pathname);
  if (/^\/ai\/c\/[^/]+$/.test(path)) { return 'ai'; }
  return PATH_TO_VIEW[path] || DEFAULT_VIEW;
}

export function readPortalConversationId(pathname) {
  const path = normalizePathname(pathname);
  const match = path.match(/^\/ai\/c\/([^/]+)$/);
  if (!match) { return null; }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function buildPortalPath(view, conversationId = null) {
  if (view === 'ai' && conversationId) {
    return `/ai/c/${encodeURIComponent(conversationId)}`;
  }
  return VIEW_TO_PATH[view] || VIEW_TO_PATH[DEFAULT_VIEW];
}

// The marketing site the cup mark links back to from pre-auth portal screens.
// Env-driven so local dev can point at the Expo web client.
export function marketingHomeUrl() {
  return import.meta.env.VITE_MARKETING_URL || 'https://coffeechats.me';
}

export function sanitizePortalReturnPath(value) {
  try {
    const target = new URL(String(value || VIEW_TO_PATH[DEFAULT_VIEW]), window.location.origin);
    if (target.origin !== window.location.origin || (readPortalView(target.pathname) === DEFAULT_VIEW
      && normalizePathname(target.pathname) !== VIEW_TO_PATH[DEFAULT_VIEW])) {
      return VIEW_TO_PATH[DEFAULT_VIEW];
    }
    return `${normalizePathname(target.pathname)}${target.search}`;
  } catch {
    return VIEW_TO_PATH[DEFAULT_VIEW];
  }
}

// The URL of a school's own portal subdomain, e.g. https://dartmouth.coffeechats.me.
export function schoolPortalUrl(slug, location = window.location, returnPath = '/') {
  const hostname = String(location.hostname || '').toLowerCase();
  const path = returnPath === '/' ? '/' : sanitizePortalReturnPath(returnPath);
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    const port = location.port ? `:${location.port}` : '';
    return `${location.protocol || 'http:'}//${slug}.localhost${port}${path}`;
  }
  return `https://${slug}.coffeechats.me${path === '/' ? '' : path}`;
}

// The school's portal URL as seen from ANOTHER origin, specifically the console
// on admin.coffeechats.me (port 3200 in local dev). schoolPortalUrl derives the
// dev port from the caller's location, which is 3100 for every portal-side
// caller but 3200 here, which would point the link at the console's own bundle
// under a school hostname. Pin resolution to the portal's fixed origin instead.
export function schoolPortalUrlFromAnyOrigin(slug) {
  const origin = import.meta.env.VITE_PORTAL_AUTH_ORIGIN || 'https://portal.coffeechats.me';
  return schoolPortalUrl(slug, new URL(origin));
}
